import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  fetchAllUserGroups,
  selectUserGroups,
  deleteUserGroup,
  selectFetchingUserGroups,
  selectCreatingUserGroup,
  selectDeletingUserGroup,
  selectUpdatingUserGroup,
  resetUserGroup,
} from "./UserGroupSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import UserGroupModal from "./UserGroupModal";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { YesNoAlert } from "cpl-react-common/components/Alerts/YesNo";
import { useDispatch } from "react-redux";

type Props = {
  updateSelectedGroupID?: (selectedGroupID: number | null) => void;
};

const UserGroups: React.FC<Props> = ({ updateSelectedGroupID }) => {
  const thunkDispatch = useThunkDispatch();
  const dispatch = useDispatch();
  const userGroups = useSelector(selectUserGroups);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedUserGroupID, setSelectedUserGroupID] = useState<number | undefined | null>(undefined);
  const [showDeleteGroupAlert, setShowDeleteGroupAlert] = useState(false);

  const fetchingUserGroups = useSelector(selectFetchingUserGroups);
  const creatingUserGroup = useSelector(selectCreatingUserGroup);
  const deletingUserGroup = useSelector(selectDeletingUserGroup);
  const updatingUserGroup = useSelector(selectUpdatingUserGroup);

  const groupsAreBusy = fetchingUserGroups || creatingUserGroup || deletingUserGroup || updatingUserGroup;
  const currentUserGroupName = userGroups?.find((ug) => ug.userGroupID === selectedUserGroupID)?.groupName ?? "";

  useEffect(() => {
    thunkDispatch(fetchAllUserGroups());
  }, [thunkDispatch]);

  function deleteGroup(userGroupID: number) {
    thunkDispatch(deleteUserGroup(userGroupID));
    setSelectedUserGroupID(null);
    dispatch(resetUserGroup());
    if (updateSelectedGroupID !== undefined) {
      updateSelectedGroupID(null);
    }
  }

  function openRenameModal(userGroupID: number) {
    setSelectedUserGroupID(userGroupID);
    setShowGroupModal(true);
  }

  const handleCloseDeleteGroupAlert = (answer: boolean) => {
    setShowDeleteGroupAlert(false);
    if (answer && selectedUserGroupID != undefined) deleteGroup(selectedUserGroupID);
  };

  useEffect(() => {
    if (selectedUserGroupID != undefined && updateSelectedGroupID !== undefined) {
      updateSelectedGroupID(selectedUserGroupID);
    }
  }, [thunkDispatch, selectedUserGroupID, updateSelectedGroupID]);

  return (
    <Permission permissionPredicate={(permission) => permission === Permissions.ViewUserGroups}>
      <YesNoAlert
        showAlert={showDeleteGroupAlert}
        alertTitle={`Delete User Group`}
        alertText={`Are you sure you want to delete the user group "${currentUserGroupName}"?`}
        callBack={handleCloseDeleteGroupAlert}
        style={Style.Warning}
      />
      <div className="card">
        <ActivityIndicator isBusy={groupsAreBusy}>
          <Permission permissionPredicate={(permission) => permission === Permissions.ManageUserGroups}>
            <UserGroupModal
              showGroupModal={showGroupModal}
              setShowGroupModal={setShowGroupModal}
              updateSelectedGroupID={updateSelectedGroupID}
              userGroupID={selectedUserGroupID}
              setSelectedUserGroupID={setSelectedUserGroupID}
            />
          </Permission>
          <div className="card-header">
            <div className="card-title">User Groups</div>
            <div className="card-toolbar">
              <Permission permissionPredicate={(permission) => permission === Permissions.ManageUserGroups}>
                <button
                  data-test-id="user-groups-table__add-group-button"
                  type="button"
                  className="btn btn-primary btn-icon btn-sm"
                  onClick={() => {
                    setSelectedUserGroupID(null);
                    setShowGroupModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus as IconProp} />
                </button>
              </Permission>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                <tbody>
                  {!groupsAreBusy &&
                    userGroups?.map((row) => (
                      <tr key={row.userGroupID}>
                        <td
                          data-test-id={`user-groups-table__select-group-cell-${row.userGroupID}`}
                          className={`${
                            selectedUserGroupID === row.userGroupID ? "text-primary" : "text-dark"
                          } fw-bold text-hover-primary mb-1 fs-6 cursor-pointer`}
                          onClick={() => setSelectedUserGroupID(row.userGroupID!)}
                        >
                          {row.groupName}
                        </td>
                        <td className="text-end">
                          <Permission permissionPredicate={(permission) => permission === Permissions.ManageUserGroups}>
                            <button
                              data-test-id={`user-groups-table__rename-button-${row.userGroupID}`}
                              className="btn btn-primary btn-sm btn-icon border-0 me-1"
                              onClick={() => openRenameModal(row.userGroupID!)}
                            >
                              <FontAwesomeIcon icon={faEdit as IconProp} />
                            </button>
                            <button
                              data-test-id={`user-groups-table__delete-button-${row.userGroupID}`}
                              className="btn btn-danger btn-sm btn-icon border-0 me-1"
                              onClick={() => {
                                setSelectedUserGroupID(row.userGroupID!);
                                setShowDeleteGroupAlert(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash as IconProp} />
                            </button>
                          </Permission>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </ActivityIndicator>
      </div>
    </Permission>
  );
};

export default UserGroups;
